import { Box, Chip, Collapse, IconButton, TableCell, TableRow, Typography, Link as MuiLink } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useState } from 'react'
import { GiftCardJournalAction, GiftCardJournalItem, GiftCardPaymentStatus } from 'shared/models'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import { jounalActionText, jounalInitiatorText } from './utils'
import { giftCardStatusBackground, giftCardStatusColor, giftCardStatusText, paymentStatusText } from '../status-utils'

function GiftCardJournalTableItem({ item }: { item: GiftCardJournalItem }) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset !important' } }}>
        <TableCell style={{ width: '36px' }}>
          {[
            GiftCardJournalAction.GiftCardCreation,
            GiftCardJournalAction.PaymentConfirmed,
            GiftCardJournalAction.PaymentRejected,
            GiftCardJournalAction.PaymentRequested
          ].includes(item.actionType) && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell align="left" width={'150px'}>
          {Intl.DateTimeFormat('ru', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }).format(item.dateTime)}
        </TableCell>
        <TableCell align="left" width={'230px'}>
          {jounalActionText[item.actionType] || item.actionType}
        </TableCell>
        <TableCell align="left" width={'250px'}>
          <div>{jounalInitiatorText[item.actionInitiator] || item.actionInitiator}</div>
          {!!item.user && (
            <div>
              <Typography variant="caption">{item.user.email}</Typography>
            </div>
          )}
          {!!item.user && (
            <div>
              <Typography variant="caption">
                {[item.user.lastName, item.user.firstName].filter(Boolean).join(' ')}
              </Typography>
            </div>
          )}
        </TableCell>
        <TableCell align="left">
          {item.actionType === GiftCardJournalAction.GiftCardStaffCommentChanged && (
            <div>{item.giftCard.staffComment}</div>
          )}
          {item.actionType === GiftCardJournalAction.GiftCardStatusChanged && !!item.giftCard.status && (
            <Chip
              label={giftCardStatusText[item.giftCard.status]}
              size="small"
              style={{
                backgroundColor: giftCardStatusBackground[item.giftCard.status],
                color: giftCardStatusColor[item.giftCard.status]
              }}
            />
          )}
          {item.actionType === GiftCardJournalAction.GiftCardPaymentChanged && !!item.giftCard.status && (
            <Chip label={GiftCardPaymentStatus[item.giftCard.paymentStatus]} size="small" />
          )}
          {item.actionType === GiftCardJournalAction.GiftCardCancellation && (
            <Chip
              label={giftCardStatusText['Cancelled']}
              size="small"
              style={{
                backgroundColor: giftCardStatusBackground['Cancelled'],
                color: giftCardStatusColor['Cancelled']
              }}
            />
          )}
          {item.actionType === GiftCardJournalAction.GiftCardRedeem && (
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap="0.25rem">
              <Chip
                label={giftCardStatusText['Used']}
                size="small"
                style={{
                  backgroundColor: giftCardStatusBackground['Used'],
                  color: giftCardStatusColor['Used']
                }}
              />
              {!!item.force && <Typography variant="caption">Без пин-кода</Typography>}
            </Box>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              {[
                GiftCardJournalAction.PaymentRequested,
                GiftCardJournalAction.PaymentRejected,
                GiftCardJournalAction.PaymentConfirmed
              ].includes(item.actionType) &&
                !!item.paymentInfo && (
                  <div style={{ padding: '0.5rem 1rem' }}>
                    <div>
                      Сумма: <span style={{ color: '#5567f5' }}>{item.paymentInfo.amount} ₽</span>
                    </div>
                    <div>
                      Ссылка на оплату:{' '}
                      <Typography
                        component="a"
                        variant="body2"
                        target="_blank"
                        href={item.paymentInfo.link}
                        style={{ marginTop: '1rem' }}
                      >
                        {item.paymentInfo.link}
                      </Typography>
                    </div>
                  </div>
                )}
              {[GiftCardJournalAction.GiftCardCreation].includes(item.actionType) && !!item.giftCard && (
                <div style={{ padding: '0.5rem 1rem' }}>
                  <div>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {item.giftCard.name}
                      </Typography>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '2px' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <PhoneIcon fontSize="small" style={{ opacity: 0.72, width: '16px', height: '16px' }} />
                          <MuiLink style={{ fontSize: '13px' }} href={`tel:${item.giftCard.phoneNumber}`}>
                            {item.giftCard.phoneNumber}
                          </MuiLink>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <EmailIcon fontSize="small" style={{ opacity: 0.72, width: '16px', height: '16px' }} />
                          <MuiLink style={{ fontSize: '13px' }} href={`mailto:${item.giftCard.email}`}>
                            {item.giftCard.email}
                          </MuiLink>
                        </div>
                      </div>
                    </Box>
                    <Box marginTop="1rem">
                      <Typography variant="caption">Номер</Typography>
                      <Typography>{item.giftCard.code}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption">Номинал</Typography>
                      <Typography>{item.giftCard.amount} ₽</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption">Оплата</Typography>
                      <Typography>{paymentStatusText[item.giftCard.paymentStatus] || 'Не запрашивалась'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption">Срок действия</Typography>
                      <Typography>
                        {Intl.DateTimeFormat('ru', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        }).format(item.giftCard.expiryDate)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption">Комментарий сотрудника</Typography>
                      <Typography style={{ opacity: item.giftCard.staffComment ? 1 : 0.72 }}>
                        {item.giftCard.staffComment || 'Не указан'}
                      </Typography>
                    </Box>
                  </div>
                </div>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export { GiftCardJournalTableItem }
